import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  Typography,
  DialogActions,
  Button,
  Box,
  Fab,
  useMediaQuery,
  useTheme,
  List,
  ListItemText,
  Grid,
  Skeleton,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import { cancelBooking, fetchBookingsFreeCancel } from '../../api';
import { useHookState } from '../../NotificationApiHook';
import BookingStatusInfo from './BookingStatusInfo';
import {
  BookingDto,
  getDayMonth,
  getTime,
  responseToMap,
  getFreeCancelStatus,
  GroupedBookings,
} from './utils';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import {
  BookingsModalDialogTitle,
  BookingsModalListItemText,
} from './CanceledBookingModal/CanceledBookingModal.styled';

export interface CancelBookingModalProps {
  selectedGroups: GroupedBookings[];
  onSuccessCancel: () => void;
  forceCancelModalOpenCounter: number;
  handleCancelModalClose: () => void;
}

const CancelBookingModal = ({
  selectedGroups,
  onSuccessCancel,
  forceCancelModalOpenCounter,
  handleCancelModalClose,
}: CancelBookingModalProps) => {
  const isAdmin = localStorage.getItem('role_id') === '1';
  const isCoach = localStorage.getItem('role_id') === '3';
  const isMobile = useMediaQuery('(max-width:600px)');
  const theme = useTheme();
  const { isLoggedIn, token } = useContext(AuthContext);
  const { notificationState } = useHookState();
  const fabVisible = selectedGroups.length > 0;

  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);
  const [cancelForYear, setCancelForYear] = useState(false);
  const [isLoadingFreeCancelState, setIsLoadingFreeCancelState] = useState(true);
  const [freeCancelMap, setFreeCancelMap] = useState<Map<number, boolean>>();
  const [selectedBookings, setSelectedBookings] = useState<BookingDto[]>([]);

  const handleOpenCancelDialog = () => {
    setCancelBookingDialogOpen(true);
    setSelectedBookings(selectedGroups?.map((group) => group.bookings).flat());
  };
  const handleCloseCancelDialog = () => {
    setCancelBookingDialogOpen(false);
    setFreeCancelMap(new Map());
    handleCancelModalClose();
    setSelectedBookings([]);
  };

  const handleBookingClick = (booking: BookingDto) => {
    setSelectedBookings((prevBlocks) => {
      const blockIndex = prevBlocks.findIndex((selectedBlock) => selectedBlock.id === booking.id);
      if (blockIndex !== -1) {
        // Если блок уже выбран, снимаем его выбор
        return prevBlocks.filter((selectedBlock) => selectedBlock.id !== booking.id);
      } else {
        // Если блок еще не выбран, добавляем его в список выбранных
        return [...prevBlocks, booking];
      }
    });
  };

  const handleConfirmCancelBooking = async () => {
    const bookingsToCancel = selectedBookings.map((booking) => ({
      id: booking.id,
      time_range_id: booking.time_range_id,
      table_number: booking.table_number,
      place_id: booking.place_id,
      amount: booking.amount,
    }));
    try {
      await cancelBooking(token, bookingsToCancel, cancelForYear, notificationState);
      onSuccessCancel();
      setCancelForYear(false);
      setCancelBookingDialogOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (cancelBookingDialogOpen) {
      setIsLoadingFreeCancelState(true);
      const selectedBlooksIds = selectedGroups
        .map((block) => block.bookings.map((booking) => booking.id))
        .flat();
      fetchBookingsFreeCancel(token, notificationState, selectedBlooksIds)
        .then((data) => {
          const freeCancelMap = responseToMap(data);
          setFreeCancelMap(freeCancelMap);
        })
        .finally(() => setIsLoadingFreeCancelState(false));
    }
  }, [cancelBookingDialogOpen, selectedGroups]);

  useEffect(() => {
    if (forceCancelModalOpenCounter > 0) {
      setCancelBookingDialogOpen(true);
    }
  }, [forceCancelModalOpenCounter]);

  return (
    <>
      <Dialog
        open={cancelBookingDialogOpen}
        onClose={handleCloseCancelDialog}
        sx={{ '.MuiPaper-root': { margin: 2, flexGrow: 1 } }}
      >
        <BookingsModalDialogTitle>Подтверждение отмены брони</BookingsModalDialogTitle>

        <DialogContent sx={{ padding: '10 36px' }}>
          {(isAdmin || isCoach) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={cancelForYear}
                  onChange={() => setCancelForYear(!cancelForYear)}
                />
              }
              label={`Отменить все брони на ${
                selectedGroups.length === 1
                  ? 'это выбранное время?'
                  : 'эти выбранные блоки времени?'
              }`}
            />
          )}
          <Typography
            sx={{
              textAlign: 'center',
              padding: '10px',
            }}
          >
            {`Вы уверены, что хотите отменить ${selectedGroups.length === 1 ? 'бронь' : 'брони'}?`}
          </Typography>
          <List>
            {selectedGroups.map((block, index) => (
              <React.Fragment key={block.id}>
                {block?.bookings?.map((booking) => (
                  <Grid
                    container
                    key={booking.id}
                    sx={{ alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => handleBookingClick(booking)}
                  >
                    <Grid
                      item
                      xs={1}
                    >
                      <Checkbox
                        checked={selectedBookings.some(
                          (selectedBooking) => selectedBooking.id === booking.id,
                        )}
                        icon={<CircleOutlinedIcon />}
                        checkedIcon={<CheckCircleOutlineOutlinedIcon />}
                        sx={{ padding: 0 }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={3}
                    >
                      <BookingsModalListItemText
                        primary={`${getDayMonth(booking.day.replace(/"/g, ''))} `}
                        secondary={`${getTime(booking.start_time)} - ${getTime(booking.end_time)}`}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={3}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography>{`Cтол ${booking.table_number}`}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {isLoadingFreeCancelState ? (
                        <Skeleton
                          height={24}
                          width={120}
                        />
                      ) : (
                        <>
                          {booking.id && freeCancelMap?.has(booking.id) && (
                            <BookingStatusInfo
                              status={getFreeCancelStatus(freeCancelMap.get(booking.id))}
                              index={index}
                            />
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ padding: '0' }}>
          <Button
            onClick={handleCloseCancelDialog}
            color='primary'
            sx={{ padding: '10' }}
          >
            Назад
          </Button>
          <Button
            onClick={handleConfirmCancelBooking}
            color='primary'
            variant='contained'
            disabled={isLoadingFreeCancelState || selectedBookings.length === 0}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: '0px',
              paddingTop: 2,
              paddingBottom: 2,
              width: '75%',
              '&.Mui-disabled': {
                backgroundColor: '#4b4b4b',
                color: theme.palette.primary.contrastText,
              },
              '&:hover': {
                backgroundColor: '#8e80d7',
              },
            }}
          >
            Подтвердить отмену
          </Button>
        </DialogActions>
      </Dialog>
      <Fab
        color='primary'
        onClick={handleOpenCancelDialog}
        disabled={!isLoggedIn}
        sx={{
          backgroundColor: theme.palette.primary.main,
          position: 'sticky',
          bottom: '58px',
          marginLeft: isMobile ? '0' : 'calc(50% - 275px)',
          display: fabVisible ? 'block' : 'none',
          width: '100%',
          maxWidth: '600px',
          height: '80px',
          lineHeight: '30px',
          padding: '15px',
          zIndex: '100',
          borderRadius: '0',
          '&.Mui-disabled': {
            backgroundColor: '#4b4b4b',
            color: theme.palette.primary.contrastText,
          },
        }}
      >
        {isLoggedIn ? <Box>Отменить бронь</Box> : <Box>Войдите, чтобы увидеть брони</Box>}
      </Fab>
    </>
  );
};

export default React.memo(CancelBookingModal);
